import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';
import './AdminManagement.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
const [resetPasswordModal, setResetPasswordModal] = useState({ show: false, userId: null, userEmail: '' });
const [newPassword, setNewPassword] = useState('');
  const [newUser, setNewUser] = useState({
    nom: '',
    prenom: '',
    email: '',
    motdepasse: '',
    genre: '',
    idrole: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/admin');
      return;
    }
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getUsers();
      setUsers(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await adminAPI.updateUserRole(userId, newRole);
      await loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStatusToggle = async (userId) => {
    try {
      await adminAPI.toggleUserStatus(userId);
      await loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
    try {
      await adminAPI.deleteUser(userId);
      await loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResetPasswordClick = (user) => {
    setResetPasswordModal({ show: true, userId: user.id, userEmail: user.email });
    setNewPassword('');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const { userId } = resetPasswordModal;
    if (!newPassword || newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    try {
      const result = await adminAPI.resetUserPassword(userId, newPassword);
      if (result.success) {
        setResetPasswordModal({ show: false, userId: null, userEmail: '' });
        alert('Mot de passe réinitialisé avec succès');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  const openAddModal = () => {
    setNewUser({
      nom: '',
      prenom: '',
      email: '',
      motdepasse: '',
      genre: '',
      idrole: ''
    });
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setNewUser({
      nom: '',
      prenom: '',
      email: '',
      motdepasse: '',
      genre: '',
      idrole: ''
    });
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await adminAPI.createUser(newUser);
      await loadUsers();
      closeAddModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || 
                          (filterStatus === 'active' && user.isActive) ||
                          (filterStatus === 'inactive' && !user.isActive);
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Get role counts
  const roleCounts = {
    Administrateur: users.filter(u => u.role === 'Administrateur').length,
    Presentateur: users.filter(u => u.role === 'Presentateur').length,
    moderator: users.filter(u => u.role === 'moderator').length,
    user: users.filter(u => u.role === 'user').length,
  };

  const statusCounts = {
    active: users.filter(u => u.isActive).length,
    inactive: users.filter(u => !u.isActive).length,
    verified: users.filter(u => u.isVerified).length,
  };

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  return (
    <div className="management-page">
      <div className="management-header">
        <div>
          <h1>Gestion des Utilisateurs</h1>
          <p>Gérez les utilisateurs de l'application</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          + Ajouter un utilisateur
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Utilisateurs</h3>
          <p className="stat-value">{users.length}</p>
        </div>
        <div className="stat-card">
          <h3>Actifs</h3>
          <p className="stat-value">{statusCounts.active}</p>
        </div>
        <div className="stat-card">
          <h3>Inactifs</h3>
          <p className="stat-value">{statusCounts.inactive}</p>
        </div>
        <div className="stat-card">
          <h3>Vérifiés</h3>
          <p className="stat-value">{statusCounts.verified}</p>
        </div>
      </div>

      {/* Role Distribution */}
      <div className="role-distribution">
        <h3>Répartition par Rôle</h3>
        <div className="role-bars">
          <div className="role-bar">
            <span className="role-label">Administrateur</span>
            <div className="role-bar-container">
              <div 
                className="role-bar-fill admin" 
                style={{ width: `${(roleCounts.Administrateur / users.length) * 100}%` }}
              />
            </div>
            <span className="role-count">{roleCounts.Administrateur}</span>
          </div>
          <div className="role-bar">
            <span className="role-label">Présentateur</span>
            <div className="role-bar-container">
              <div 
                className="role-bar-fill presenter" 
                style={{ width: `${(roleCounts.Presentateur / users.length) * 100}%` }}
              />
            </div>
            <span className="role-count">{roleCounts.Presentateur}</span>
          </div>
          <div className="role-bar">
            <span className="role-label">Modérateur</span>
            <div className="role-bar-container">
              <div 
                className="role-bar-fill moderator" 
                style={{ width: `${(roleCounts.moderator / users.length) * 100}%` }}
              />
            </div>
            <span className="role-count">{roleCounts.moderator}</span>
          </div>
          <div className="role-bar">
            <span className="role-label">Utilisateur</span>
            <div className="role-bar-container">
              <div 
                className="role-bar-fill user" 
                style={{ width: `${(roleCounts.user / users.length) * 100}%` }}
              />
            </div>
            <span className="role-count">{roleCounts.user}</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Rechercher par nom ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-selects">
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
            <option value="all">Tous les rôles</option>
            <option value="Administrateur">Administrateur</option>
            <option value="Presentateur">Présentateur</option>
            <option value="moderator">Modérateur</option>
            <option value="user">Utilisateur</option>
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Email</th>
              <th>Rôle</th>
              <th>Statut</th>
              <th>Vérifié</th>
              <th>Dernière connexion</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id}>
                <td>
                  <div className="user-cell">
                    <div className="user-avatar">
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                    <span>{user.name}</span>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>
                  <select
                    value={user.role}
                    onChange={(e) => handleRoleChange(user.id, e.target.value)}
                    className={`role-select ${user.role.toLowerCase()}`}
                  >
                    <option value="Administrateur">Administrateur</option>
                    <option value="Presentateur">Présentateur</option>
                    <option value="moderator">Modérateur</option>
                    <option value="user">Utilisateur</option>
                  </select>
                </td>
                <td>
                  <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                    {user.isActive ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${user.isVerified ? 'verified' : 'unverified'}`}>
                    {user.isVerified ? 'Vérifié' : 'Non vérifié'}
                  </span>
                </td>
                <td>
                  {user.lastLogin 
                    ? new Date(user.lastLogin).toLocaleDateString('fr-FR')
                    : 'Jamais'}
                </td>
<td>
                   <div className="action-buttons">
                     <button
                       className="btn-icon"
                       onClick={() => handleViewUser(user)}
                       title="Voir détails"
                     >
                       👁
                     </button>
                     <button
                       className="btn-icon"
                       onClick={() => handleResetPasswordClick(user)}
                       title="Réinitialiser mot de passe"
                     >
                       🔑
                     </button>
                     <button
                       className="btn-icon"
                       onClick={() => handleStatusToggle(user.id)}
                       title={user.isActive ? 'Désactiver' : 'Activer'}
                     >
                       {user.isActive ? '⏸' : '▶'}
                     </button>
                     <button
                       className="btn-icon btn-danger"
                       onClick={() => handleDeleteUser(user.id)}
                       title="Supprimer"
                     >
                       🗑
                     </button>
                   </div>
                 </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredUsers.length === 0 && (
          <div className="no-data">Aucun utilisateur trouvé</div>
        )}
      </div>

      {/* User Details Modal */}
      {showModal && selectedUser && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Détails de l'utilisateur</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="user-details">
                <div className="detail-row">
                  <span className="detail-label">ID:</span>
                  <span className="detail-value">{selectedUser.id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Nom:</span>
                  <span className="detail-value">{selectedUser.name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{selectedUser.email}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Rôle:</span>
                  <span className="detail-value">{selectedUser.role}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Statut:</span>
                  <span className="detail-value">
                    {selectedUser.isActive ? 'Actif' : 'Inactif'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Vérifié:</span>
                  <span className="detail-value">
                    {selectedUser.isVerified ? 'Oui' : 'Non'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">2FA:</span>
                  <span className="detail-value">
                    {selectedUser.twoFactorEnabled ? 'Activé' : 'Désactivé'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Créé le:</span>
                  <span className="detail-value">
                    {new Date(selectedUser.createdAt).toLocaleString('fr-FR')}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Dernière connexion:</span>
                  <span className="detail-value">
                    {selectedUser.lastLogin 
                      ? new Date(selectedUser.lastLogin).toLocaleString('fr-FR')
                      : 'Jamais'}
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeModal}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
       {resetPasswordModal.show && (
         <div className="modal-overlay" onClick={() => setResetPasswordModal({ show: false, userId: null, userEmail: '' })}>
           <div className="modal-content" onClick={(e) => e.stopPropagation()}>
             <div className="modal-header">
               <h2>Réinitialiser le mot de passe</h2>
               <button className="modal-close" onClick={() => setResetPasswordModal({ show: false, userId: null, userEmail: '' })}>×</button>
             </div>
             <form onSubmit={handleResetPassword}>
               <div className="modal-body">
                 <p>Utilisateur : <strong>{resetPasswordModal.userEmail}</strong></p>
                 <div className="form-group">
                   <label>Nouveau mot de passe *</label>
                   <input
                     type="password"
                     value={newPassword}
                     onChange={(e) => setNewPassword(e.target.value)}
                     required
                     minLength={6}
                     placeholder="Entrez le nouveau mot de passe"
                   />
                 </div>
               </div>
               <div className="modal-footer">
                 <button type="button" className="btn-secondary" onClick={() => setResetPasswordModal({ show: false, userId: null, userEmail: '' })}>
                   Annuler
                 </button>
                 <button type="submit" className="btn-primary">Réinitialiser</button>
               </div>
             </form>
           </div>
         </div>
       )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeAddModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Ajouter un utilisateur</h2>
              <button className="modal-close" onClick={closeAddModal}>×</button>
            </div>
            <form onSubmit={handleAddUser}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nom *</label>
                  <input
                    type="text"
                    value={newUser.nom}
                    onChange={(e) => setNewUser({ ...newUser, nom: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Prénom</label>
                  <input
                    type="text"
                    value={newUser.prenom}
                    onChange={(e) => setNewUser({ ...newUser, prenom: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Mot de passe *</label>
                  <input
                    type="password"
                    value={newUser.motdepasse}
                    onChange={(e) => setNewUser({ ...newUser, motdepasse: e.target.value })}
                    required
                    minLength={6}
                  />
                </div>
                <div className="form-group">
                  <label>Genre</label>
                  <select
                    value={newUser.genre}
                    onChange={(e) => setNewUser({ ...newUser, genre: e.target.value })}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="Homme">Homme</option>
                    <option value="Femme">Femme</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Rôle</label>
                  <select
                    value={newUser.idrole}
                    onChange={(e) => setNewUser({ ...newUser, idrole: e.target.value })}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="1">Administrateur</option>
                    <option value="2">Présentateur</option>
                    <option value="3">Modérateur</option>
                    <option value="4">Utilisateur</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeAddModal}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Création...' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
